import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  ConsumerScheduleCompletedInterFace,
  RequestConsumerScheduleCompletedInterFace,
} from '../../../../interface/end-consumer-schedule-completed-interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setSelectedDevice } from '../../../../redux/slices/DeviceManagementSlice';
import {
  fetchConsumerScheduleCompleted,
  fetchConsumerScheduleCompletedFilter,
  setSortData,
  updateConsumerScheduleCompletedListFilter,
} from '../../../../redux/slices/EndConsumerScheduleCompleted';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import ChargingSessionSidePanel from '../../../../shared-components/table-side-panel/ChargingSessionSidePanel';
import DeviceSidePanel from '../../../../shared-components/table-side-panel/DeviceSidePanel';
import SidePaneModel from '../../../../shared-components/table-side-panel/SidePaneModel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import { convertUtcTo12HourTime } from '../../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import userApiV2 from '../../../../utils/api/end-consumer-v2';

interface ModelDetailsInterFace {
  title?: string;
  content: JSX.Element;
  url?: string;
  urlNewTab?: string;
}

/**
 * Charge Session List
 * @returns
 */
function ScheduleCompletedList() {
  const { t } = useTranslation('endConsumer');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [modelDetails, setModelDetails] = useState<ModelDetailsInterFace>({
    title: '',
    content: <div />,
    url: '',
    urlNewTab: '',
  });
  const {
    consumers,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.ConsumerScheduleCompleted);
  const { selectedEndConsumer, endConsumerType } = useAppSelector((state) => state.endConsumerType);

  /**
   * Fetch Users list function
   */
  const fetchData = (data: RequestConsumerScheduleCompletedInterFace) => {
    if (!loading && selectedEndConsumer) {
      dispatch(
        fetchConsumerScheduleCompleted({
          userCustomId: selectedEndConsumer.userCustomId,
          status: 'completed',
          ...data,
        }),
      );
    }
  };

  /**
   * Handle open device details
   * @param data - {ConsumerScheduleCompletedInterFace}
   */
  const handleOpenDeviceDetails = (data: ConsumerScheduleCompletedInterFace) => {
    navigate(`?deviceId=${data?.chargingPointSn}`);
    setIsOpen(true);
    dispatch(
      setSelectedDevice({
        id: data?.chargingPointId,
        name: data?.chargingPointName,
        chargingPointSn: data?.chargingPointSn,
      }),
    );
    setModelDetails({
      content: <DeviceSidePanel fetchIdFrom="queryParams" />,
      url: `/ev-admin/end-consumer/${endConsumerType}/profile/${selectedEndConsumer?.userCustomId}/device/${data?.chargingPointId}/device-details?searchBy=${data?.chargingPointName}`,
      urlNewTab: `/ev-admin/end-consumer/${endConsumerType}/profile/${selectedEndConsumer?.userCustomId}/device-expanded/${data?.chargingPointId}/device-details?searchBy=${data?.chargingPointName}`,
    });
  };

  /**
   * Handle open power consumed details
   * @param data - {ConsumerScheduleCompletedInterFace}
   */
  const handleOpenPowerConsumedDetails = (data: ConsumerScheduleCompletedInterFace) => {
    navigate(`?stationId=${data?.chargingPointSn}`);
    setIsOpen(true);
    setModelDetails({
      title: t('chargingSessionDetails'),
      content: <ChargingSessionSidePanel fetchIdFrom="queryParams" />,
    });
  };

  const column = [
    {
      label: t('startDateTime'),
      accessorKey: 'scheduleStartDateUtc' as keyof ConsumerScheduleCompletedInterFace,
      sortAllow: true,
      /**
       * Custom data functionacc
       * @param currentRow - current row data
       * @returns - custom data
       */
      customData: (currentRow: ConsumerScheduleCompletedInterFace) => (
        <div>
          <p>{ConvertUTCtoDate(currentRow?.scheduleStartDateUtc)}</p>
          <p>{convertUtcTo12HourTime(currentRow?.scheduleStartDateUtc)}</p>
        </div>
      ),
    },
    {
      label: t('endDateTime'),
      accessorKey: 'scheduleEndDateUtc' as keyof ConsumerScheduleCompletedInterFace,
      sortAllow: true,
      /**
       * Custom data function
       * @param currentRow - current row data
       * @returns - custom data
       */
      customData: (currentRow: ConsumerScheduleCompletedInterFace) => (
        <div>
          <p>{ConvertUTCtoDate(currentRow?.scheduleEndDateUtc)}</p>
          <p>{convertUtcTo12HourTime(currentRow?.scheduleEndDateUtc)}</p>
        </div>
      ),
    },
    {
      label: t('device'),
      accessorKey: 'chargingPointSn' as keyof ConsumerScheduleCompletedInterFace,
      sortAllow: false,
      /**
       * Rendering device name as hyper link
       * @param currentRow - {ConsumerScheduleCompletedInterFace}
       * @returns - {JSX.Element}
       */
      customData: (currentRow: ConsumerScheduleCompletedInterFace) => (
        <p
          role="presentation"
          className="hyper-link"
          onClick={() => handleOpenDeviceDetails(currentRow)}>
          {currentRow?.chargingPointSn}
        </p>
      ),
    },
    {
      label: t('powerConsumed'),
      accessorKey: 'maximumOutputPower' as keyof ConsumerScheduleCompletedInterFace,
      sortAllow: true,
      /**
       * Rendering power consumed as hyper link
       * @param currentRow - {ConsumerScheduleCompletedInterFace}
       * @returns - {JSX.Element}
       */
      customData: (currentRow: ConsumerScheduleCompletedInterFace) => (
        <div className="value-container">
          <p
            role="presentation"
            className="hyper-link value"
            onClick={() => handleOpenPowerConsumedDetails(currentRow)}>
            {currentRow?.maximumOutputPower ?? 0}
          </p>
          <span>kW</span>
        </div>
      ),
    },
  ];

  /**
   * Current columns for Schedule Completed export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('startDateTime'), accessorKey: 'scheduleStartDateUtc' },
    { label: t('endDateTime'), accessorKey: 'scheduleEndDateUtc' },
    { label: t('device'), accessorKey: 'chargingPointSn' },
    { label: t('powerConsumed'), accessorKey: 'maximumOutputPower' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);

    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload: any = { ...queryParameters };

    if (sortField) payload.sortField = sortField;
    if (sortBy) payload.sortBy = sortBy;

    payload.status = 'completed';

    if (!selectedEndConsumer?.userCustomId) return;

    userApiV2
      .fetchSchedulesExportData(selectedEndConsumer.userCustomId, payload)
      .then((response) => {
        /**
         * Dynamic Headers
         */
        const headers = exportCurrentColumns.map((col) => col.label);

        /**
         * Dynamic Rows
         */
        const rows = response.data.map((row: ConsumerScheduleCompletedInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'scheduleStartDateUtc':
                return row?.scheduleStartDateUtc
                  ? `${ConvertUTCtoDate(
                      row.scheduleStartDateUtc,
                    )} ${convertUtcTo12HourTime(row.scheduleStartDateUtc)}`
                  : 'N/A';

              case 'scheduleEndDateUtc':
                return row?.scheduleEndDateUtc
                  ? `${ConvertUTCtoDate(
                      row.scheduleEndDateUtc,
                    )} ${convertUtcTo12HourTime(row.scheduleEndDateUtc)}`
                  : 'N/A';

              case 'chargingPointSn':
                return row?.chargingPointSn || 'N/A';

              case 'maximumOutputPower':
                return row?.maximumOutputPower || 'N/A';

              default:
                return (row as any)[col.accessorKey] || 'N/A';
            }
          }),
        );

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'reports-list.csv');
          } else {
            exportPDF(headers, rows, 'reports-list.pdf');
          }
        }

        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Table pagination function
   * @param value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, ...value, sortField, sortBy });
  };

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  /**
   * Handle the close function
   */
  const handleClose = () => {
    setIsOpen(false);
    setModelDetails({ title: '', content: <div /> });
  };

  useEffect(() => {
    if (!selectedEndConsumer?.userCustomId) return;

    const currentId = selectedEndConsumer.userCustomId;
    const lastId = sessionStorage.getItem('scheduleCompletedList');

    if (isFilterUpdated || consumers.length === 0 || lastId !== currentId) {
      if (selectedEndConsumer?.userCustomId) {
        const queryParameters = GetParametersFromCommonFilters(filters);
        fetchData({
          page: DefaultApiLimit.page,
          limit,
          ...queryParameters,
        });
      }
    }
    sessionStorage.setItem('scheduleCompletedList', currentId);
  }, [filters, selectedEndConsumer]);

  return (
    <div>
      <SidePaneModel
        open={isOpen}
        title={modelDetails.title}
        content={modelDetails.content}
        url={modelDetails.url}
        closePanel={handleClose}
        urlNewTab={modelDetails.urlNewTab}
      />
      <CustomTable
        header={column}
        data={consumers}
        serialNo
        actionButton
        sizeChangeButtonRequired
        version="v2"
        height={window.innerHeight >= 700 ? 325 : 327}
        page={page}
        limit={limit}
        loading={loading}
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageNateLimitChange}
        filters={filters}
        filtersToDispatch={updateConsumerScheduleCompletedListFilter}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchConsumerScheduleCompletedFilter}
        exportData={consumers}
        exportLoader={exportLoader}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        refreshRequired
        ReferenceCurrentPages={handleRefresh}
        hoverLabel={t('refresh')}
        leftTopContent={<> </>}
        expandTableOpen
        expandTableOpenKey="ScheduleCompletedV2ListEndConsumer"
        onHiddenColumnsChange={setHiddenColumns}
      />{' '}
    </div>
  );
}

export default ScheduleCompletedList;
